import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
} from '@backstage/integration-react';
import {
  analyticsApiRef,
  AnyApiFactory,
  configApiRef,
  createApiFactory,
  discoveryApiRef,
  errorApiRef,
  fetchApiRef,
  identityApiRef,
  oauthRequestApiRef,
} from '@backstage/core-plugin-api';
import {
  apiDocsConfigRef,
  defaultDefinitionWidgets,
} from '@backstage/plugin-api-docs';
import { grpcDocsApiWidget } from '@backstage/plugin-api-docs-module-protoc-gen-doc';
import { ApiEntity } from '@backstage/catalog-model';
import { searchApiRef } from '@backstage/plugin-search-react';
import { SearchClient } from './apis/searchClient';
import { FcmAPI, fcmApiRef } from '@internal/backstage-plugin-notifier';
import { MatomoAnalytics } from '@backstage-community/plugin-analytics-module-matomo';
import { UmamiAnalytics } from '@axis-backstage/plugin-analytics-module-umami';
import { OAuth2, MultipleAnalyticsApi } from '@backstage/core-app-api';
import { zitadelAuthApiRef } from './auth';
import { visitsApiRef, VisitsWebStorageApi } from '@backstage/plugin-home';
import {
  umamiAnalyticsApiRef,
  UmamiAnalyticsApiImpl,
} from '@internal/backstage-plugin-umami-analytics';

export const apis: AnyApiFactory[] = [
  createApiFactory({
    api: apiDocsConfigRef,
    deps: {},
    factory: () => {
      // load the default widgets
      const definitionWidgets = defaultDefinitionWidgets();
      // add the grpc-docs api widget to the definition widgets
      definitionWidgets.push(grpcDocsApiWidget);
      return {
        getApiDefinitionWidget: (apiEntity: ApiEntity) => {
          // find the widget for the type of api entity
          return definitionWidgets.find(d => d.type === apiEntity.spec.type);
        },
      };
    },
  }),
  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),
  createApiFactory({
    api: analyticsApiRef,
    deps: {
      configApi: configApiRef,
      fetchApi: fetchApiRef,
      identityApi: identityApiRef,
    },
    factory: ({ configApi, fetchApi, identityApi }) =>
      MultipleAnalyticsApi.fromApis([
        MatomoAnalytics.fromConfig(configApi, {
          identityApi: identityApi,
        }),
        UmamiAnalytics.fromConfig(configApi, { fetchApi }),
      ]),
  }),
  ScmAuth.createDefaultApiFactory(),
  createApiFactory({
    api: searchApiRef,
    deps: {
      discovery: discoveryApiRef,
      identityApi: identityApiRef,
      config: configApiRef,
      fetchApi: fetchApiRef,
    },
    factory({ discovery, identityApi, config, fetchApi }) {
      return new SearchClient({
        discoveryApi: discovery,
        identityApi: identityApi,
        config,
        fetchApi: fetchApi,
      });
    },
  }),
  createApiFactory({
    api: fcmApiRef,
    deps: { configApi: configApiRef, fetchApi: fetchApiRef },
    factory: ({ fetchApi, configApi }) => new FcmAPI({ fetchApi, configApi }),
  }),
  createApiFactory({
    api: zitadelAuthApiRef,
    deps: {
      configApi: configApiRef,
      discoveryApi: discoveryApiRef,
      oauthRequestApi: oauthRequestApiRef,
    },
    factory: ({ configApi, discoveryApi, oauthRequestApi }) => {
      return OAuth2.create({
        configApi,
        discoveryApi,
        provider: {
          id: 'oauth2',
          title: 'Zitadel',
          icon: () => null,
        },
        oauthRequestApi,
        defaultScopes: ['openid', 'profile', 'email', 'offline_access'],
        environment: configApi.getString('auth.environment'),
      });
    },
  }),
  createApiFactory({
    api: visitsApiRef,
    deps: {
      identityApi: identityApiRef,
      errorApi: errorApiRef,
    },
    factory: ({ identityApi, errorApi }) =>
      VisitsWebStorageApi.create({ identityApi, errorApi }),
  }),
  createApiFactory({
    api: umamiAnalyticsApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
    },
    factory: ({ discoveryApi, fetchApi }) =>
      new UmamiAnalyticsApiImpl({ discoveryApi, fetchApi }),
  }),
];
